import { isEqual } from "lodash";
import { useEffect, useRef } from "react";

export enum WebsiteBuilderDebouncedUpdateTime {
  SHORT = 250,
  MEDIUM = 500,
}

interface IUseWebsiteBuilderDebouncedUpdateArgs<TState> {
  state: TState;
  updatedState: TState;
  setUpdatedState: React.Dispatch<React.SetStateAction<TState>>;
  dispatchState: (prevState: TState, nextState: TState) => void;
  debouncedUpdateTime: WebsiteBuilderDebouncedUpdateTime;
}

interface IDebounceRefState {
  timeout: NodeJS.Timeout;
  active: boolean;
}

type THandleUpdateState<TState> = (updateState: (prev: TState) => TState) => void;
type THandleDebouncedUpdateState<TState> = (updateState: (prev: TState) => TState) => void;

interface IUseWebsiteBuilderDebouncedUpdate<TState> {
  handleUpdateState: THandleUpdateState<TState>;
  handleDebouncedUpdateState: THandleDebouncedUpdateState<TState>;
}

export function useWebsiteBuilderDebouncedUpdate<TState>(
  args: IUseWebsiteBuilderDebouncedUpdateArgs<TState>,
): IUseWebsiteBuilderDebouncedUpdate<TState> {
  const debounceRefState = useRef<IDebounceRefState>({
    timeout: null,
    active: false,
  });

  useEffect(() => {
    return () => clearTimeout(debounceRefState.current.timeout);
  }, []);

  useEffect(() => {
    let mounted = true;

    const dispatchState = (prev: TState, next: TState): void => {
      if (mounted && !isEqual(prev, next)) {
        args.dispatchState(prev, next);
      }
    };

    if (debounceRefState.current.active) {
      debounceRefState.current.timeout = setTimeout(() => {
        dispatchState(args.state, args.updatedState);

        debounceRefState.current.timeout = null;
        debounceRefState.current.active = false;
      }, args.debouncedUpdateTime);
    } else {
      dispatchState(args.state, args.updatedState);
    }

    return () => {
      mounted = false;
    };
  }, [args.state, args.updatedState]);

  const handleDebouncedUpdateState: THandleDebouncedUpdateState<TState> = (updateState): void => {
    clearTimeout(debounceRefState.current.timeout);

    debounceRefState.current.timeout = null;
    debounceRefState.current.active = true;

    args.setUpdatedState(updateState);
  };

  const handleUpdateState: THandleUpdateState<TState> = (updateState): void => {
    clearTimeout(debounceRefState.current.timeout);

    debounceRefState.current.timeout = null;
    debounceRefState.current.active = false;

    args.setUpdatedState(updateState);
  };

  return {
    handleUpdateState,
    handleDebouncedUpdateState,
  };
}
